import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { getOptimizedImageUrl } from '../lib/image'
import { getCmsCollection, getCompanyInfo } from '../lib/cms'

const DEFAULT_LOGO_URL = 'https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-2863-4.jpeg'

const DEFAULT_SERVICES_NAV = [
  { name: 'Jordboring', path: '/tjenester/jordboring', icon: 'fa-solid fa-compass-drafting' },
  { name: 'Grøfterensning', path: '/tjenester/grofterensning', icon: 'fa-solid fa-water' },
  { name: 'Belægningsarbejde', path: '/tjenester/belaegningsarbejde', icon: 'fa-solid fa-cubes' },
  { name: 'Kloakarbejde', path: '/tjenester/kloakarbejde', icon: 'fa-solid fa-faucet-drip' },
  { name: 'Anlægsarbejde', path: '/tjenester/anlaegsarbejde', icon: 'fa-solid fa-trowel-bricks' },
  { name: 'Drænarbejde', path: '/tjenester/draenarbejde', icon: 'fa-solid fa-droplet-slash' },
  { name: 'Gravning og udgravning', path: '/tjenester/gravning-og-udgravning', icon: 'fa-solid fa-trowel' },
  { name: 'Funderingsarbejde', path: '/tjenester/funderingsarbejde', icon: 'fa-solid fa-monument' },
  { name: 'Jordafgravning og nivellering', path: '/tjenester/jordafgravning-og-nivellering', icon: 'fa-solid fa-layer-group' },
]

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(116)
  const [companyInfo, setCompanyInfo] = useState(null)
  const [servicesNav, setServicesNav] = useState(DEFAULT_SERVICES_NAV)
  const headerBarRef = useRef(null)
  const dropdownRef = useRef(null)
  const location = useLocation()

  useEffect(() => {
    async function loadCmsData() {
      try {
        const [info, services] = await Promise.all([
          getCompanyInfo(),
          getCmsCollection('tjenester'),
        ])
        if (info) setCompanyInfo(info)
        if (Array.isArray(services) && services.length > 0) {
          const sorted = services
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((s) => {
              const sSlug = typeof s.slug === 'object' ? s.slug?.da : s.slug
              const cleanSlug = sSlug ? sSlug.replace(/^tjeneste-|^service-/, '') : ''
              return {
                name: s.title,
                path: `/tjenester/${cleanSlug}`,
                icon: s.icon || 'fa-solid fa-gear',
              }
            })
          setServicesNav(sorted)
        }
      } catch (err) {
        console.error('Error loading header CMS data:', err)
      }
    }
    loadCmsData()
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
    setServicesDropdownOpen(false)
    setMobileServicesOpen(false)
  }, [location.pathname])

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
      if (headerBarRef.current) {
        setHeaderHeight(headerBarRef.current.offsetHeight)
      }
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    const handleResize = () => {
      if (headerBarRef.current) {
        setHeaderHeight(headerBarRef.current.offsetHeight)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isServicesActive = location.pathname.startsWith('/tjenester')
  const logoUrl = companyInfo?.logo?.[0] || DEFAULT_LOGO_URL
  const tagline = companyInfo?.topbar_tagline || 'Jordarbejde på Fyn & omegn'
  const addressText = companyInfo?.address
    ? `${companyInfo.address}${companyInfo.postal_and_city ? `, ${companyInfo.postal_and_city}` : ''}`
    : 'Krogsbjerg 1, 5683 Haarby'
  const phone = companyInfo?.phone || '55 23 69 32'
  const phoneClean = phone.replace(/[^0-9+]/g, '')
  const email = companyInfo?.email_primary || 'jc@lyn-ent.dk'

  return (
    <header className="sticky top-0 z-40 w-full bg-[#000000] shadow-sm border-b border-slate-800">
      <div ref={headerBarRef} className="w-full bg-[#000000]">
        {/* Top Bar for quick contact */}
      <div className="bg-[#0b1020] text-slate-300 text-xs py-2 border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <span className="inline-flex items-center gap-2 text-red-500 font-medium">
              <span className="whitespace-nowrap">{tagline}</span>
            </span>
            <span className="hidden sm:inline-block text-slate-600">|</span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-slate-300 whitespace-nowrap">
              {addressText}
            </span>
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            <a
              href={`tel:${phoneClean}`}
              className="inline-flex items-center gap-1.5 text-slate-200 hover:text-white transition font-medium whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-red-500" aria-hidden="true" />
              <span>{phone}</span>
            </a>
            <span className="text-slate-600">|</span>
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center gap-1.5 text-slate-200 hover:text-white transition whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-red-500" aria-hidden="true" />
              <span>{email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 transition hover:opacity-95 group focus:outline-none py-1"
            aria-label="Lyn Entreprenør ApS - Gå til forsiden"
          >
            <div className="flex items-center justify-center">
              <img
                src={getOptimizedImageUrl(logoUrl, { width: 560, height: 280, quality: 95 })}
                alt="Lyn Entreprenør ApS Logo"
                className="h-14 sm:h-16 md:h-18 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation (>= 1200px only) */}
          <nav
            aria-label="Hovednavigation"
            className="hidden min-[1200px]:flex items-center space-x-1"
          >
            {/* Tjenester with Dropdown */}
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <NavLink
                to="/tjenester"
                onClick={() => setServicesDropdownOpen(false)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition cursor-pointer ${
                  isServicesActive
                    ? 'text-white bg-red-600'
                    : 'text-slate-200 hover:text-white hover:bg-white/10'
                }`}
              >
                <span>Tjenester</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    servicesDropdownOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </NavLink>

              {/* Desktop Dropdown Menu */}
              {servicesDropdownOpen && (
                <div
                  className="absolute left-0 top-full pt-1.5 w-72 z-50 before:absolute before:-top-2 before:left-0 before:right-0 before:h-3"
                >
                  <div className="rounded-2xl bg-white p-2.5 shadow-xl ring-1 ring-slate-200 border border-slate-100 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="grid grid-cols-1 gap-1 max-h-[380px] overflow-y-auto">
                      {servicesNav.map((s) => (
                        <NavLink
                          key={s.path}
                          to={s.path}
                          onClick={() => setServicesDropdownOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center px-3 py-2 rounded-xl text-sm font-medium transition ${
                              isActive
                                ? 'bg-red-50 text-red-700 font-semibold'
                                : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                            }`
                          }
                        >
                          <span className="truncate">{s.name}</span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/billedgalleri"
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-lg text-sm font-semibold transition ${
                  isActive
                    ? 'text-white bg-red-600'
                    : 'text-slate-200 hover:text-white hover:bg-white/10'
                }`
              }
            >
              Billedgalleri
            </NavLink>

            <NavLink
              to="/prisliste"
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-lg text-sm font-semibold transition ${
                  isActive
                    ? 'text-white bg-red-600'
                    : 'text-slate-200 hover:text-white hover:bg-white/10'
                }`
              }
            >
              Prisliste
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-lg text-sm font-semibold transition ${
                  isActive
                    ? 'text-white bg-red-600'
                    : 'text-slate-200 hover:text-white hover:bg-white/10'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-lg text-sm font-semibold transition ${
                  isActive
                    ? 'text-white bg-red-600'
                    : 'text-slate-200 hover:text-white hover:bg-white/10'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Desktop Right Actions (>=1200px) */}
          <div className="hidden min-[1200px]:flex items-center gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition duration-200 hover:bg-red-700 active:scale-95"
            >
              <span>Få et tilbud</span>
            </Link>
          </div>

          {/* Mobile / Tablet Burger button (VISIBLE ONLY BELOW 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-2">
            <a
              href="tel:+4555236932"
              className="flex h-10 w-10 items-center justify-center text-red-400 text-lg hover:text-white transition"
              aria-label="Ring 55 23 69 32"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
              className="flex h-11 w-11 items-center justify-center text-white focus:outline-none"
            >
              <i
                className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-xl' : 'fa-bars text-lg'}`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Mobile Drawer Navigation (< 1200px) */}
      {mobileMenuOpen && (
        <div
          style={{ top: `${headerHeight}px` }}
          className="min-[1200px]:hidden fixed inset-x-0 bottom-0 z-50 border-t border-slate-800 bg-[#10172c] px-4 pt-3 pb-12 shadow-2xl overflow-y-auto overscroll-contain no-scrollbar animate-in fade-in duration-150"
        >
          <nav className="flex flex-col space-y-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base font-semibold transition ${
                  isActive ? 'bg-red-600 text-white' : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Hjem
            </NavLink>

            {/* Mobile Tjenester Accordion */}
            <div>
              <div className="flex items-center justify-between rounded-xl px-4 py-3 hover:bg-white/5">
                <NavLink
                  to="/tjenester"
                  className={({ isActive }) =>
                    `text-base font-semibold ${isActive ? 'text-red-400' : 'text-slate-200'}`
                  }
                >
                  Tjenester
                </NavLink>
                <button
                  type="button"
                  onClick={() => setMobileServicesOpen((prev) => !prev)}
                  aria-label="Udvid ydelser"
                  className="p-1 text-slate-400 hover:text-white"
                >
                  <i
                    className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${
                      mobileServicesOpen ? 'rotate-180' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </div>

              {mobileServicesOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-red-500/50 pl-3">
                  {servicesNav.map((s) => (
                    <NavLink
                      key={s.path}
                      to={s.path}
                      className={({ isActive }) =>
                        `block py-2 px-3 rounded-lg text-sm transition ${
                          isActive
                            ? 'font-semibold text-white bg-red-600/20'
                            : 'text-slate-300 hover:text-white hover:bg-white/5'
                        }`
                      }
                    >
                      {s.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/billedgalleri"
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base font-semibold transition ${
                  isActive ? 'bg-red-600 text-white' : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Billedgalleri
            </NavLink>

            <NavLink
              to="/prisliste"
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base font-semibold transition ${
                  isActive ? 'bg-red-600 text-white' : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Prisliste
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base font-semibold transition ${
                  isActive ? 'bg-red-600 text-white' : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-4 py-3 rounded-xl text-base font-semibold transition ${
                  isActive ? 'bg-red-600 text-white' : 'text-slate-200 hover:bg-white/10'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          <div className="mt-6 pt-6 border-t border-slate-800 space-y-3 pb-6">
            <Link
              to="/kontakt"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 py-3 text-center text-sm font-semibold text-white hover:bg-red-700 transition"
            >
              <span>Få et uforpligtende tilbud</span>
            </Link>

            <div className="rounded-xl bg-slate-900/90 p-4 border border-slate-700/80 text-xs text-slate-300 space-y-2">
              <div className="flex items-center gap-2 font-medium text-white">
                <i className="fa-solid fa-phone text-red-500" aria-hidden="true" />
                <a href="tel:+4555236932" className="whitespace-nowrap hover:text-red-400 transition">55 23 69 32</a>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <i className="fa-solid fa-envelope text-red-500" aria-hidden="true" />
                <a href="mailto:jc@lyn-ent.dk" className="whitespace-nowrap hover:text-red-400 transition">jc@lyn-ent.dk</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
